import { Core, Decorative } from "@faire/design-tokens";
import { trackBrandLeadsBrandsPageHeaderSectionView } from "@faire/web-api/events/brandLeads/view/brandsPageHeaderSection";
import { trackR2BReferralsClicksOnScheduleACallButtonClick } from "@faire/web-api/events/r2BReferrals/click/clicksOnScheduleACallButton";
import { trackR2BReferralsClicksOnSignUpToSellButtonClick } from "@faire/web-api/events/r2BReferrals/click/clicksOnSignUpToSellButton";
import { trackR2BReferralsKnownReferrerBrandSignupClick } from "@faire/web-api/events/r2BReferrals/click/knownReferrerBrandSignup";
import { trackR2BReferralsKnownReferrerDemoScheduleClick } from "@faire/web-api/events/r2BReferrals/click/knownReferrerDemoSchedule";
import { localNum } from "@faire/web/common/localization";
import { Button } from "@faire/web/slate/Button";
import { Column, Row } from "@faire/web/slate/Layout";
import { getSpacing, Spacer } from "@faire/web/slate/spacing";
import { responsiveMediaQueries } from "@faire/web/slate/Theme/responsiveMediaQueries";
import { Typography } from "@faire/web/slate/Typography";
import { StrictLocalMsg } from "@faire/web/ui/lib/localization";
import { ResponsiveContentV2 } from "@faire/web/ui/ResponsiveContentV2";
import { observer } from "mobx-react";
import React, { useEffect } from "react";
import styled from "styled-components";

import { getSettingEnableKnownReferrerAdoption } from "@faire/retailer/settings/getSettingEnableKnownReferrerAdoption";

import { useKnownReferrer } from "../useKnownReferrer";
import { useReferralDetails } from "../useReferralDetails";

const TopSectionBase: React.FC = () => {
  useEffect(() => {
    trackBrandLeadsBrandsPageHeaderSectionView();
  }, []);

  return (
    <Column
      style={{
        backgroundColor: Decorative.shade.yellow,
        width: "100%",
      }}
    >
      <HeaderContent />
      <Spacer height="7x" />
      <ResponsiveContentV2 desktopAndAbove={<Spacer height="7x" />} />
    </Column>
  );
};

export const TopSection = observer(TopSectionBase);

const SignUpToSellButton: React.FC = () => {
  const referralDetails = useReferralDetails();
  return (
    <SignUpButton
      linkTo={referralDetails?.referral_url}
      onClick={
        getSettingEnableKnownReferrerAdoption()
          ? trackR2BReferralsKnownReferrerBrandSignupClick
          : trackR2BReferralsClicksOnSignUpToSellButtonClick
      }
    >
      <StrictLocalMsg
        defaultMessage="Sign up to sell"
        description={{
          text: "Prompts the brand to sign up for Faire.",
        }}
      />
    </SignUpButton>
  );
};

const MeetBrandPartnerButton: React.FC = () => {
  const referralDetails = useReferralDetails();
  return referralDetails?.calendar_url ? (
    <>
      <BrandPartnerButton
        linkTo={referralDetails?.calendar_url}
        onClick={
          getSettingEnableKnownReferrerAdoption()
            ? trackR2BReferralsKnownReferrerDemoScheduleClick
            : trackR2BReferralsClicksOnScheduleACallButtonClick
        }
      >
        <StrictLocalMsg
          defaultMessage="Meet your brand partner"
          description={{
            text: "Prompts the brand to schedule a call with their brand partner.",
          }}
        />
      </BrandPartnerButton>
      <Spacer size="2x" />
    </>
  ) : null;
};

const CTAButtons: React.FC = () => {
  return (
    <ResponsiveContentV2
      tabletAndBelow={
        <>
          <MeetBrandPartnerButton />
          <SignUpToSellButton />
        </>
      }
      desktopAndAbove={
        <Row>
          <MeetBrandPartnerButton />
          <SignUpToSellButton />
        </Row>
      }
    />
  );
};

const TitleText = () => {
  const referralDetails = useKnownReferrer();
  if (
    getSettingEnableKnownReferrerAdoption() &&
    referralDetails.referrer_name
  ) {
    return (
      <StrictLocalMsg
        defaultMessage="{retailerCompanyName} wants to shop your brand on Faire"
        description={{
          text: "Promotional message telling brands that a certain retailer would like to shop their brand on Faire",
        }}
        values={{
          retailerCompanyName: referralDetails?.referrer_name,
        }}
      />
    );
  }
  return (
    <StrictLocalMsg
      defaultMessage="Claim your offer to get one-on-one assistance setting up your shop"
      description={{
        text: "Promotional message imploring brands to claim their limited time offer and get personal assistance from Faire while onboarding.",
      }}
    />
  );
};

const MobileTitle: React.FC = () => {
  return (
    <Column align="flex-start">
      <Typography variant="displaySSerifRegular" color={Core.text.inverse}>
        <TitleText />
      </Typography>
      <Spacer height="2x" />
      <BrandsPageSubTitle />
      <Spacer height="2x" />
      <CTAButtons />
    </Column>
  );
};

const BrandsDesktopTitle: React.FC = () => {
  return (
    <Column align="center">
      <DesktopTitle
        variant="displayLSerifRegular"
        color={Core.text.inverse}
        align="center"
      >
        <TitleText />
      </DesktopTitle>
      <Spacer height="3x" />
      <BrandsPageSubTitle />
      <Spacer height="3x" />
      <CTAButtons />
    </Column>
  );
};

const BrandsPageTitle: React.FC = () => {
  return (
    <ResponsiveContentV2
      tabletAndBelow={<MobileTitle />}
      desktopAndAbove={<BrandsDesktopTitle />}
    />
  );
};

const SubtitleTextKnownRetailers = () => {
  const { number_of_referrals } = useKnownReferrer();

  if (number_of_referrals && number_of_referrals !== 0) {
    return (
      <StrictLocalMsg
        defaultMessage="{referralsNumber, plural, one{You’ve already been referred {referralsNumber} time. Set up a quick call with your brand partner to learn how they can help you build your catalog and get your shop all set up.} other{You’ve already been referred {referralsNumber} times. Set up a quick call with your brand partner to learn how they can help you build your catalog and get your shop all set up.}}"
        description={{
          text: "Message letting the brand know that retailers would like to shop their products on Faire and are provding them with a limited time offer to get one-on-one assistance from brand partners at Faire.",
        }}
        values={{
          referralsNumber: localNum(number_of_referrals),
        }}
      />
    );
  }

  return (
    <StrictLocalMsg
      defaultMessage="Set up a quick call with your brand partner to learn how they can help you build your catalog and get your shop all set up."
      description={{
        text: "Message letting the brand know that retailers would like to shop their products on Faire and are provding them with a limited time offer to get one-on-one assistance from brand partners at Faire.",
      }}
    />
  );
};

const BrandsPageSubTitle: React.FC = () => {
  return (
    <ResponsiveContentV2
      tabletAndBelow={
        <Typography
          variant="paragraphSansRegular"
          align="left"
          color={Core.text.inverse}
        >
          {getSettingEnableKnownReferrerAdoption() ? (
            <SubtitleTextKnownRetailers />
          ) : null}
        </Typography>
      }
      desktopAndAbove={
        <DesktopSubTitle
          variant="subheadingSansRegular"
          align="center"
          color={Core.text.inverse}
        >
          {getSettingEnableKnownReferrerAdoption() ? (
            <SubtitleTextKnownRetailers />
          ) : null}
        </DesktopSubTitle>
      }
    />
  );
};

const HeaderContentBase: React.FC = () => {
  return (
    <>
      <BrandsPageTitleWrapper>
        <BrandsPageTitle />
      </BrandsPageTitleWrapper>
    </>
  );
};

const HeaderContent = observer(HeaderContentBase);

const DesktopTitle = styled(Typography)`
  max-width: 780px;
`;

const DesktopSubTitle = styled(Typography)`
  max-width: 780px;
`;

const BrandsPageTitleWrapper = styled(Column).attrs({
  justify: { mobileAndAbove: "left", tabletAndAbove: "center" },
  align: { mobileAndAbove: "flex-start", tabletAndAbove: "center" },
})`
  width: 100%;
  padding: ${getSpacing(0, "3x")};
`;

const BrandPartnerButton = styled(Button).attrs({
  variant: "secondary",
  target: "_blank",
})`
  width: max-content;
  color: ${Core.text.default};
  background-color: ${Core.surface.inverse};

  ${responsiveMediaQueries.mobile} {
    width: 100%;
    justify-content: center;
    align-items: center;
    padding: 0;
  }
`;

const SignUpButton = styled(Button).attrs({
  variant: "secondary",
  target: "_blank",
})`
  width: max-content;
  color: ${Core.text.inverse};
  background-color: transparent;
  border: 1px solid ${Core.border.muted};

  ${responsiveMediaQueries.mobile} {
    width: 100%;
    justify-content: center;
    align-items: center;
    padding: 0;
  }
`;
